/* ============================================================
   Lettoon collection pages runtime (Collection Pages canvas
   1c/1e/1a/1d + 2a popover + 3a sheet).

   Vanilla IIFE, defensive no-ops: every feature bails when its
   DOM is absent, and every control is a real link first —
   filtering, sorting and load-more all work with JS disabled.

   Enhancements:
   1. Quick add  — single-variant AJAX add, then the builder's
      proven drawer sequence: fetch /cart.js, dispatch
      theme:cart:change with the cart payload, dispatch
      theme:drawer:open on the drawer root (lt-pack-cart.js
      listens on both).
   2. Lean variant picker (fix round 3, 2026-07-19) — the
      multi-variant "+ Add" link (data-lt-vpick) opens a small
      chooser fed by the card's JSON blob: bottom sheet under
      750px, popover anchored on the trigger on desktop (one
      element, two presentations). Tapping a pill adds that
      variant through the SAME addToCart() path as 1. Scrim +
      scroll lock + Escape close; focus moves in on open and
      returns to the trigger on close. Without JS the control
      stays a plain link to the product page.
   3. <details> dropdowns (sort, More filters) — outside click
      and Escape close.
   4. Filter & sort sheet — scroll lock, scrim/Escape close,
      batched selection: option taps toggle locally and the
      Apply link's href is recomposed; without JS each option
      navigates immediately (server-side filtering).
   5. Load more (all breakpoints since fix round 3; desktop
      dropped the circled numbers) — fetch the next page,
      append grid cards (grouped-by-drop pages merge per
      group), update "Showing X of Y". Falls back to full
      navigation.
   6. New Releases type pills — client-side show/hide by
      product type; group counts update; empty groups hide.
   ============================================================ */
(function () {
  'use strict';

  if (window.__ltCollection) return; // both sections load this file; run once
  window.__ltCollection = 'collection-2026-07-19';

  var doc = document;

  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // #143 round 2: runtime strings this file writes into the DOM (quick-add
  // feedback, filter-sheet apply, load-more announcement, New Releases piece
  // counts) were still hardcoded English — round 1 covered every OTHER
  // lt-*.liquid/js file but named this one as a gap. Same bridge contract as
  // every other JSON-bridged file in this ticket: a `[data-lt-col-strings]`
  // script tag (added by section-lt-collection.liquid / section-lt-
  // collection-new.liquid) supplies the map; absent node / bad JSON -> stays
  // empty -> LT() falls back to today's English literal, i.e. no behaviour
  // change. lt-collection.js is ALSO loaded by section-lt-search.liquid and
  // section-lt-product.liquid (its "you may also like" grid), which are NOT
  // in this round's files_modified — those two surfaces keep the English
  // fallback for these specific runtime strings until a future round adds
  // the bridge there too (flagged in the round-2 report, not a regression:
  // identical to today's hardcoded-English behaviour on those two pages).
  var ltColStrings = {};
  (function parseColStrings() {
    var node = doc.querySelector('[data-lt-col-strings]');
    if (!node) return;
    var parsed;
    try { parsed = JSON.parse(node.textContent); } catch (e) { return; }
    if (parsed && parsed.strings) ltColStrings = parsed.strings;
  })();
  function LT(key, fallback) {
    var v = ltColStrings[key];
    if (!v) return fallback;
    // Vera B2 (2026-09-26): Shopify's `t` filter HTML-escapes every non-_html locale
    // value before `json` wraps it, so "you've" arrived as "you&#39;ve" and textContent
    // showed the entity literally. Decode once here; _html keys are real HTML already.
    if (typeof v !== 'string' || v.indexOf('&') === -1 || /_html$/.test(key)) return v;
    var ta = document.createElement('textarea');
    ta.innerHTML = v;
    return ta.value;
  }

  /* #68 finding 17: shared Tab-containment for the two aria-modal panels in
     this file (lean variant picker + filter/sort sheet) — neither trapped
     Tab before this round, so it could escape onto page chrome underneath. */
  function focusableIn(container) {
    return [].slice.call(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }
  function trapTab(e, container) {
    if (e.key !== 'Tab') return;
    var items = focusableIn(container);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------ 1. quick add */

  /* Shared add path (single-variant button AND picker pills): AJAX add,
     then the builder's proven drawer sequence — fetch /cart.js, dispatch
     theme:cart:change with the payload, theme:drawer:open on the drawer
     root. Resolves after the drawer has been asked to open; rejects when
     the add itself failed. */
  /* #143 Vera B1 (2026-09-26): cart routes must carry the locale prefix
     (/nl/cart.js returns /nl/products/... line URLs; /cart.js returns English
     ones and the drawer then links a Dutch customer to the English PDP). */
  var LT_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  /* Quantity = the variant's order minimum (data-qty-min on the button, `min`
     in the picker JSON), which the card Liquid reads from the quantity rule.
     A consumer's minimum is 1, so their add is byte-identical to before. A
     wholesale buyer's minimum is 5: an add of 1 was refused by Shopify and
     the button looked dead (Millenotes, 2026-10-12). Missing or broken value
     -> 1, exactly the old behaviour. */
  function qtyFor(raw) {
    var n = parseInt(raw, 10);
    return n >= 1 ? n : 1;
  }
  function addToCart(variantId, quantity) {
    var added = null; // the add response line: rides along on theme:cart:change
                      // so the drawer pins the just-added item (#18 r3e)
    return fetch(LT_ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qtyFor(quantity) })
    })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function (res) {
        // `{id}` body -> item object; `{items:[..]}` body -> { items: [...] }
        var item = res && res.items ? res.items[0] : res;
        if (item) added = { key: item.key || null, variantId: item.variant_id || item.id || null };
        return fetch(LT_ROOT + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .catch(function () { return null; });
      })
      .then(function (cart) {
        if (cart) {
          var detail = { cart: cart };
          if (added) detail.added = added;
          doc.dispatchEvent(new CustomEvent('theme:cart:change', { detail: detail, bubbles: true }));
        } else {
          doc.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));
        }
        var drawer = doc.querySelector('[data-drawer="drawer-cart"]');
        if (drawer) drawer.dispatchEvent(new CustomEvent('theme:drawer:open'));
      });
  }

  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-lt-qadd]');
    if (!btn || btn.tagName !== 'BUTTON') return; // <a> variant navigates
    e.preventDefault();
    if (btn.disabled) return;
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;
    var quantity = qtyFor(btn.getAttribute('data-qty-min'));

    var label = btn.querySelector('[data-lt-qadd-label]');
    var glyph = btn.querySelector('[data-lt-qadd-glyph]');
    var labelWas = label ? label.textContent : '';
    var glyphWas = glyph ? glyph.textContent : '';
    btn.disabled = true;
    if (label) label.textContent = LT('adding', 'Adding');

    addToCart(variantId, quantity)
      .then(function () {
        btn.classList.add('is-added');
        if (label) label.textContent = LT('added', '✓ Added');
        if (glyph) glyph.textContent = '✓';
        window.setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (label) label.textContent = labelWas;
          if (glyph) glyph.textContent = glyphWas;
        }, 1600);
      })
      .catch(function () {
        // A refused add used to reset the button silently, which reads as
        // "nothing happened". Say it, briefly, then restore.
        btn.classList.add('is-failed');
        if (label) label.textContent = LT('add_failed', 'Not added');
        if (glyph) glyph.textContent = '!';
        window.setTimeout(function () {
          btn.classList.remove('is-failed');
          btn.disabled = false;
          if (label) label.textContent = labelWas;
          if (glyph) glyph.textContent = glyphWas;
        }, 1600);
      });
  });

  /* ------------------------------------------------ 2. lean variant picker */

  /* One shared element per page, built on first open and repopulated from
     the clicked card's JSON blob. Same internals, two presentations: the
     CSS turns it into a bottom sheet under 750px; on desktop the panel is
     anchored on the trigger here (the card has overflow:hidden, so the
     panel lives on a fixed full-viewport root, not inside the card). */
  var vpickRoot = null;
  var vpickPanel = null;
  var vpickList = null;
  var vpickTitle = null;
  var vpickTrigger = null;

  function vpickLock(on) {
    doc.documentElement.classList[on ? 'add' : 'remove']('lt-col-lock');
  }

  function vpickBuild() {
    if (vpickRoot) return;
    vpickRoot = doc.createElement('div');
    vpickRoot.className = 'lt-col__vpick';
    vpickRoot.hidden = true;
    vpickRoot.innerHTML =
      '<div class="lt-col__vpickscrim" data-lt-vpick-scrim></div>' +
      '<div class="lt-col__vpickpanel" role="dialog" aria-modal="true" aria-labelledby="lt-vpick-title" data-lt-vpick-panel>' +
        '<div class="lt-col__sheetgrab lt-col__vpickgrab" aria-hidden="true"><span></span></div>' +
        '<div class="lt-col__vpickhead">' +
          '<span class="lt-col__vpicktitle lt-display" id="lt-vpick-title" data-lt-vpick-title></span>' +
          '<button type="button" class="lt-col__vpickclose" data-lt-vpick-close aria-label="' + LT('close', 'Close') + '">&times;</button>' +
        '</div>' +
        '<div class="lt-col__vpicklist" data-lt-vpick-list></div>' +
      '</div>';
    doc.body.appendChild(vpickRoot);
    vpickPanel = vpickRoot.querySelector('[data-lt-vpick-panel]');
    vpickList = vpickRoot.querySelector('[data-lt-vpick-list]');
    vpickTitle = vpickRoot.querySelector('[data-lt-vpick-title]');

    vpickRoot.querySelector('[data-lt-vpick-scrim]').addEventListener('click', vpickClose);
    vpickRoot.querySelector('[data-lt-vpick-close]').addEventListener('click', vpickClose);

    vpickList.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('[data-lt-vpick-variant]');
      if (!pill || pill.disabled) return;
      var variantId = parseInt(pill.getAttribute('data-lt-vpick-variant'), 10);
      if (!variantId) return;
      var quantity = qtyFor(pill.getAttribute('data-lt-vpick-min'));
      var price = pill.querySelector('.lt-col__vpillprice');
      var priceWas = price ? price.textContent : '';
      pill.classList.add('is-busy');
      if (price) price.textContent = LT('adding', 'Adding');
      addToCart(variantId, quantity)
        .then(function () {
          vpickClose(); // drawer opens over the grid; focus returns to the trigger
          pill.classList.remove('is-busy');
          if (price) price.textContent = priceWas;
        })
        .catch(function () {
          pill.classList.remove('is-busy');
          if (price) price.textContent = LT('add_failed', 'Not added');
          window.setTimeout(function () {
            if (price) price.textContent = priceWas;
          }, 1600);
        });
    });
  }

  function vpickOpen(trigger, data) {
    vpickBuild();
    vpickTrigger = trigger;
    vpickTitle.textContent = data.title || '';
    vpickList.textContent = '';
    (data.variants || []).forEach(function (v) {
      var pill = doc.createElement('button');
      pill.type = 'button';
      pill.className = 'lt-col__vpill';
      pill.setAttribute('data-lt-vpick-variant', String(v.id));
      pill.setAttribute('data-lt-vpick-min', String(qtyFor(v.min)));
      if (!v.available) pill.disabled = true;
      var name = doc.createElement('span');
      name.className = 'lt-col__vpillname';
      name.textContent = v.title;
      var price = doc.createElement('span');
      price.className = 'lt-col__vpillprice';
      price.textContent = v.available ? v.price : LT('sold_out', 'Sold out');
      pill.appendChild(name);
      pill.appendChild(price);
      vpickList.appendChild(pill);
    });

    vpickRoot.hidden = false;
    vpickLock(true);
    trigger.setAttribute('aria-expanded', 'true');

    // desktop: anchor the panel on the trigger, clamped to the viewport;
    // mobile: the CSS bottom sheet owns the geometry (clear inline coords)
    vpickPanel.style.left = '';
    vpickPanel.style.top = '';
    if (window.matchMedia && window.matchMedia('(min-width: 750px)').matches) {
      var r = trigger.getBoundingClientRect();
      var pw = vpickPanel.offsetWidth;
      var ph = vpickPanel.offsetHeight;
      var left = Math.max(12, Math.min(r.right - pw, window.innerWidth - pw - 12));
      var top = r.bottom + 8;
      if (top + ph > window.innerHeight - 12) top = Math.max(12, r.top - ph - 8);
      vpickPanel.style.left = left + 'px';
      vpickPanel.style.top = top + 'px';
    }

    var first = vpickList.querySelector('button:not([disabled])') ||
      vpickRoot.querySelector('[data-lt-vpick-close]');
    if (first) first.focus();
  }

  function vpickClose() {
    if (!vpickRoot || vpickRoot.hidden) return;
    vpickRoot.hidden = true;
    vpickLock(false);
    if (vpickTrigger) {
      vpickTrigger.setAttribute('aria-expanded', 'false');
      vpickTrigger.focus();
      vpickTrigger = null;
    }
  }

  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-lt-vpick]');
    if (!trigger) return;
    var card = trigger.closest('.lt-col__card');
    var blob = card && card.querySelector('script[data-lt-vpick-data]');
    if (!blob) return; // no data — stay a plain link
    var data;
    try { data = JSON.parse(blob.textContent); } catch (err) { return; }
    if (!data || !data.variants || !data.variants.length) return;
    e.preventDefault();
    vpickOpen(trigger, data);
  });

  doc.addEventListener('keyup', function (e) {
    if (e.key === 'Escape') vpickClose();
  });
  doc.addEventListener('keydown', function (e) {
    if (vpickRoot && !vpickRoot.hidden) trapTab(e, vpickPanel);
  });

  /* ------------------------------------------------ 3. dropdowns */

  ready(function () {
    var dropdowns = [].slice.call(doc.querySelectorAll('details[data-lt-dropdown]'));
    if (dropdowns.length) {
      doc.addEventListener('click', function (e) {
        dropdowns.forEach(function (d) {
          if (d.open && !d.contains(e.target)) d.removeAttribute('open');
        });
      });
      doc.addEventListener('keyup', function (e) {
        if (e.key !== 'Escape') return;
        dropdowns.forEach(function (d) { if (d.open) d.removeAttribute('open'); });
      });
    }
  });

  /* ------------------------------------------------ 4. filter sheet */

  ready(function () {
    var sheetD = doc.querySelector('details[data-lt-sheet]');
    if (!sheetD) return;
    var panel = sheetD.querySelector('[data-lt-sheet-panel]');
    var scrim = sheetD.querySelector('[data-lt-sheet-scrim]');
    if (!panel) return;

    /* #88 round 4 (structural fix, Timothy): the sheet + scrim used to live
       as position:fixed DOM descendants of .lt-col__chiprow, a scrolling
       row (overflow-x:auto on mobile). Four independent diagnosis rounds
       proved the sheet's own code renders correctly in every test engine,
       yet nothing showed on the founder's real iPhone — the working theory
       is that some ancestor becomes a stacking context / containing block
       on his iOS version (transform/filter/backdrop-filter/contain/
       will-change all qualify), which turns "fixed" into "positioned
       relative to that small scrolling row" and clips it to a sliver. No
       test engine ever triggered that; his device did. Portaling scrim +
       panel to be direct children of <body> removes the whole ancestor
       chain, so the risk class is moot regardless of which property was
       the actual trigger. Done once here (not per-open) since it's a pure
       DOM move — event listeners below are wired to these same node
       references, so they keep working wherever the nodes live; closeBtn/
       applyLink are looked up via `panel` (not `sheetD`) for the same
       reason, since they're panel descendants, not sheetD descendants. */
    if (scrim) doc.body.appendChild(scrim);
    doc.body.appendChild(panel);

    var closeBtn = panel.querySelector('[data-lt-sheet-close]');
    var applyLink = panel.querySelector('[data-lt-apply]');

    /* Round-4 regression fix (Vera NO-SHIP, same day): inside <details> the
       closed state hid these nodes for free; once portaled to <body> that
       native content-hiding is gone and .lt-col__sheet's own display:flex
       painted sheet + scrim permanently, burying the page at rest. Their
       visibility now follows sheetD.open explicitly via [hidden] (CSS
       partner rule raises specificity over the display:flex base rule).
       Deliberately NOT gated on html.lt-col-lock — that class is shared
       with the variant picker, which would reveal this sheet whenever a
       picker opens. */
    function syncSheetVisibility() {
      var open = !!sheetD.open;
      panel.hidden = !open;
      if (scrim) scrim.hidden = !open;
    }
    syncSheetVisibility();

    var baseUrl = panel.getAttribute('data-base-url') || window.location.pathname;
    var dirty = false;

    function lock(on) {
      doc.documentElement.classList[on ? 'add' : 'remove']('lt-col-lock');
    }
    sheetD.addEventListener('toggle', function () { lock(sheetD.open); syncSheetVisibility(); });
    function close() {
      // #68 finding 17: removeAttribute('open') can collapse <details>
      // content while focus is still inside it (Chrome leaves focus
      // "stuck" on the now-hidden node) — check first, restore to the
      // summary trigger after.
      var focusWasInside = panel.contains(doc.activeElement);
      sheetD.removeAttribute('open');
      lock(false);
      if (focusWasInside) {
        var summary = sheetD.querySelector('summary');
        if (summary) summary.focus();
      }
    }
    if (scrim) scrim.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    doc.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && sheetD.open) close();
    });
    doc.addEventListener('keydown', function (e) {
      if (sheetD.open) trapTab(e, panel);
    });

    function composeUrl() {
      var handles = [].slice.call(panel.querySelectorAll('[data-lt-opt].is-on'))
        .map(function (a) { return a.getAttribute('data-handle'); })
        .filter(Boolean);
      var sortEl = panel.querySelector('[data-lt-sortopt].is-on');
      var sort = sortEl ? sortEl.getAttribute('data-sort') : '';
      var url = baseUrl.replace(/\/$/, '');
      if (handles.length) url += '/' + handles.join('+');
      if (sort) url += '?sort_by=' + encodeURIComponent(sort);
      return url;
    }

    function markDirty() {
      dirty = true;
      if (applyLink) {
        applyLink.href = composeUrl();
        applyLink.textContent = LT('apply_filters', 'Apply filters');
      }
    }

    panel.addEventListener('click', function (e) {
      var opt = e.target.closest && e.target.closest('[data-lt-opt]');
      if (opt) {
        e.preventDefault();
        opt.classList.toggle('is-on');
        var isOn = opt.classList.contains('is-on');
        // #68 finding 7: multi-select checkbox toggle — aria-pressed synced
        // on every click (server render sets the initial state).
        opt.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        var box = opt.querySelector('.lt-col__checkbox');
        if (box) box.textContent = isOn ? '✓' : '';
        markDirty();
        return;
      }
      var sortOpt = e.target.closest && e.target.closest('[data-lt-sortopt]');
      if (sortOpt) {
        e.preventDefault();
        // #68 finding 7/24: single-select — aria-current follows is-on
        [].slice.call(panel.querySelectorAll('[data-lt-sortopt]')).forEach(function (s) {
          s.classList.remove('is-on');
          s.removeAttribute('aria-current');
        });
        sortOpt.classList.add('is-on');
        sortOpt.setAttribute('aria-current', 'true');
        markDirty();
        return;
      }
      var clear = e.target.closest && e.target.closest('[data-lt-sheet-clear]');
      if (clear && dirty) {
        // JS mode: reset local selection without leaving the page
        e.preventDefault();
        [].slice.call(panel.querySelectorAll('[data-lt-opt].is-on')).forEach(function (a) {
          a.classList.remove('is-on');
          a.setAttribute('aria-pressed', 'false');
          var box = a.querySelector('.lt-col__checkbox');
          if (box) box.textContent = '';
        });
        markDirty();
      }
    });
  });

  /* ------------------------------------------------ 5. load more */

  ready(function () {
    var btn = doc.querySelector('[data-lt-loadmore]');
    if (!btn || !window.fetch || !window.DOMParser) return;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.classList.contains('is-busy')) return;
      btn.classList.add('is-busy');
      var url = btn.getAttribute('href');

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { if (!r.ok) throw new Error('page'); return r.text(); })
        .then(function (html) {
          var next = new DOMParser().parseFromString(html, 'text/html');
          var added = 0;
          var firstNewCard = null; // #68 finding 13: focus target when the button retires

          var newGroups = [].slice.call(next.querySelectorAll('[data-lt-group]'));
          if (newGroups.length) {
            // New Releases: merge per drop group
            var anchor = btn.closest('.lt-col__loadmore');
            newGroups.forEach(function (g) {
              var key = g.getAttribute('data-lt-group');
              var cards = [].slice.call(g.querySelectorAll('.lt-col__card'));
              added += cards.length;
              var existing = doc.querySelector('[data-lt-group="' + key + '"] [data-lt-grid]');
              if (existing) {
                cards.forEach(function (c) {
                  if (!firstNewCard) firstNewCard = c;
                  existing.appendChild(c);
                });
                var cnt = doc.querySelector('[data-lt-group="' + key + '"] [data-lt-groupcount]');
                if (cnt) {
                  var base = parseInt(cnt.getAttribute('data-basecount'), 10) || 0;
                  var total = base + cards.length;
                  cnt.setAttribute('data-basecount', String(total));
                  cnt.textContent = total + ' ' + (total === 1 ? LT('piece_singular', 'piece') : LT('piece_plural', 'pieces'));
                }
              } else if (anchor && anchor.parentNode) {
                if (!firstNewCard) firstNewCard = g.querySelector('.lt-col__card');
                anchor.parentNode.insertBefore(g, anchor);
              }
            });
          } else {
            // standard grid append
            var grid = doc.querySelector('[data-lt-grid]');
            var newGrid = next.querySelector('[data-lt-grid]');
            if (!grid || !newGrid) { window.location.href = url; return; }
            [].slice.call(newGrid.children).forEach(function (c) {
              added += 1;
              if (!firstNewCard) firstNewCard = c;
              grid.appendChild(c);
            });
          }

          // aria-live="polite" on the span (section markup) announces the
          // load without wrapping the whole grid (finding 8). #124: used to
          // read "Showing X of Y" computed from data-shown/data-total, but
          // both numbers traced back to collection.products_count /
          // paginate.items, which count hidden lt-internal placeholders the
          // grid never renders — an inflated total on every collection or
          // search that carries one. Non-numeric message instead, so the
          // wrong count can't come back through a Load more click.
          var showing = doc.querySelector('[data-lt-showing]');
          if (showing) {
            showing.textContent = LT('more_designs_loaded', 'More designs loaded');
          }

          // advance or retire the button
          var nextBtn = next.querySelector('[data-lt-loadmore]');
          if (nextBtn && nextBtn.getAttribute('href')) {
            btn.setAttribute('href', nextBtn.getAttribute('href'));
            btn.classList.remove('is-busy');
          } else {
            // #68 finding 13: btn.remove() dropped focus with nowhere to
            // land on the last page. Move it to the first freshly-appended
            // card instead (tabindex="-1" makes a plain div a valid target).
            if (firstNewCard) {
              firstNewCard.setAttribute('tabindex', '-1');
              firstNewCard.focus();
            }
            btn.remove();
          }
        })
        .catch(function () {
          // graceful fallback: behave like the plain link
          window.location.href = url;
        });
    });
  });

  /* ------------------------------------------------ 6. NR type pills */

  ready(function () {
    var pills = [].slice.call(doc.querySelectorAll('[data-lt-typepill]'));
    if (!pills.length) return;

    function applyType(type) {
      var cards = [].slice.call(doc.querySelectorAll('[data-lt-ptype]'));
      cards.forEach(function (c) {
        var show = type === 'all' || c.getAttribute('data-lt-ptype') === type;
        c.classList.toggle('is-typehidden', !show);
      });
      [].slice.call(doc.querySelectorAll('[data-lt-group]')).forEach(function (g) {
        var visible = g.querySelectorAll('[data-lt-ptype]:not(.is-typehidden)').length;
        g.classList.toggle('is-typehidden', visible === 0);
        var cnt = g.querySelector('[data-lt-groupcount]');
        if (cnt) {
          if (type === 'all') {
            var base = parseInt(cnt.getAttribute('data-basecount'), 10) || 0;
            cnt.textContent = base + ' ' + (base === 1 ? LT('piece_singular', 'piece') : LT('piece_plural', 'pieces'));
          } else {
            cnt.textContent = visible + ' ' + (visible === 1 ? LT('piece_singular', 'piece') : LT('piece_plural', 'pieces'));
          }
        }
      });
    }

    pills.forEach(function (p) {
      p.addEventListener('click', function () {
        // #68 finding 7: single-select toggle buttons — aria-pressed kept in
        // sync with the visual .is-active state on every click.
        pills.forEach(function (q) {
          q.classList.remove('is-active');
          q.setAttribute('aria-pressed', 'false');
        });
        p.classList.add('is-active');
        p.setAttribute('aria-pressed', 'true');
        applyType(p.getAttribute('data-type') || 'all');
      });
    });
  });

})();
